import { Box, Center, Divider, Flex, Group, Modal, Stack } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { RevisionTechnicianSelect } from 'components/selects/RevisionTechnicianSelect';
import { useDisclosure } from '@mantine/hooks';
import { useApi } from 'api/api-context';
import { showNotification } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import panic from 'errors/panic';
import { noop } from 'lodash';
import Avatar from 'components/Avatar';
import P2Medium from 'components/typography/P2Medium';
import P3Medium from 'components/typography/P3Medium';
import { IconTrash, IconRefresh } from '@tabler/icons-react';
import P1Medium from 'components/typography/P1Medium';
import P2Regular from 'components/typography/P2Regular';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';

/**
 * Delegated user information.
 */
export interface DelegatedUser {
  userId: number;
  fullName: string;
  email: string;
  state: 'pending' | 'accepted' | 'rejected';
}

/**
 * Parameters for the DelegateModal component.
 */
export interface DelegateModalProps {
  opened: boolean;
  title: string;
  revisionKind: string;
  revisionId: number;
  technicianId: number;
  departmentId: number;
  deviceTypeId: number;
  delegatedUsers?: DelegatedUser[];
  onClose: () => void;
  onDelegate?: () => void;
}

/**
 * Modal for managing revision delegations.
 */
export default function DelegateModal({
  opened,
  title,
  revisionKind,
  revisionId,
  departmentId,
  deviceTypeId,
  technicianId,
  delegatedUsers = [],
  onClose,
  onDelegate = noop,
}: DelegateModalProps) {
  const { getAction } = useApi();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);

  // Local state for delegated users list
  const [localDelegatedUsers, setLocalDelegatedUsers] = useState<DelegatedUser[]>(delegatedUsers);

  // Sync local state with prop changes (when modal opens with fresh data)
  useEffect(() => {
    setLocalDelegatedUsers(delegatedUsers);
  }, [delegatedUsers, opened]);

  // Exclude technician and already delegated users
  const excludeTechnicianIds = useMemo(
    () => [technicianId, ...localDelegatedUsers.map((u) => u.userId)],
    [technicianId, localDelegatedUsers]
  );

  /**
   * Get badge color based on delegation state.
   */
  const getStateColor = (state: string) => {
    switch (state) {
      case 'accepted':
        return { bg: 'teal.0', text: 'teal.9', border: 'teal.2' };
      case 'rejected':
        return { bg: 'red.0', text: 'red.9', border: 'red.2' };
      default:
        return { bg: 'gray.1', text: 'gray.8', border: 'gray.3' };
    }
  };

  /**
   * Get badge label based on delegation state.
   */
  const getStateLabel = (state: string) => {
    switch (state) {
      case 'accepted':
        return 'Akceptované';
      case 'rejected':
        return 'Zamietnuté';
      default:
        return 'Čaká sa';
    }
  };

  /**
   * Handles adding a new delegation.
   */
  const handleDelegate = async () => {
    if (!selectedTechnicianId) return;

    startLoading();
    try {
      const delegate = getAction('RevisionDelegate');

      // Delegate the revision
      const technician = await delegate({
        parameters: { revisionId: String(revisionId) },
        payload: { delegatedTo: Number(selectedTechnicianId) },
      });

      showNotification({
        title: 'Úspešné delegovanie',
        message:
          revisionKind === 'revision' ? 'Revízna správa bola úspešne delegovaná.' : 'Formulár bol úspešne delegovaný.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      // Add the new user to the local list immediately
      const newUser: DelegatedUser = {
        userId: technician.userId,
        fullName: technician.fullName,
        email: technician.email,
        state: 'pending',
      };

      setLocalDelegatedUsers((prev) => [...prev, newUser]);
      setSelectedTechnicianId(null);
      onDelegate();
    } catch (error) {
      panic(error as Error);
    } finally {
      stopLoading();
    }
  };

  /**
   * Handles removing a delegation.
   */
  const handleRemove = async (userId: number) => {
    setRemovingUserId(userId);
    try {
      const delegateRemove = getAction('RevisionDelegateRemove');

      await delegateRemove({
        parameters: { revisionId: String(revisionId) },
        payload: { delegatedUserId: userId },
      });

      showNotification({
        title: 'Delegovanie zrušené',
        message: 'Delegovanie bolo úspešne odstránené.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      // Remove user from local list immediately
      setLocalDelegatedUsers((prev) => prev.filter((u) => u.userId !== userId));

      onDelegate();
    } catch (error) {
      panic(error as Error);
    } finally {
      setRemovingUserId(null);
    }
  };

  /**
   * Handles re-requesting delegation for rejected users.
   */
  const handleReDelegate = async (userId: number) => {
    setRemovingUserId(userId);
    try {
      const delegateRemove = getAction('RevisionDelegateRemove');
      const delegate = getAction('RevisionDelegate');

      // First remove, then re-create
      await delegateRemove({
        parameters: { revisionId: String(revisionId) },
        payload: { delegatedUserId: userId },
      });

      await delegate({
        parameters: { revisionId: String(revisionId) },
        payload: { delegatedTo: userId },
      });

      showNotification({
        title: 'Opätovné delegovanie',
        message: 'Delegovanie bolo úspešne obnovené.',
        color: SUCCESS_NOTIFICATION_COLOR,
      });

      // Update user state to pending in local list immediately
      setLocalDelegatedUsers((prev) =>
        prev.map((u) => (u.userId === userId ? { ...u, state: 'pending' as const } : u))
      );

      onDelegate();
    } catch (error) {
      panic(error as Error);
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing={8} align="center">
          {title}
          <Center pt={2}>
            <QuestionMarkTooltip
              size={18}
              withinPortal
              multiline
              width={300}
              label={
                revisionKind === 'revision'
                  ? 'Delegovaním revíznej správy dávate možnosť inému revíznemu technikovi vypracovať túto revíziu. Odovzdanie revíznej správy môže vždy vykonať iba pridelený technik.'
                  : 'Delegovaním formulára dávate možnosť inému vypracovateľovi vypracovať tento formulár. Odovzdanie formulára môže vždy vykonať iba pridelený vypracovateľ.'
              }
            />
          </Center>
        </Group>
      }
      centered
      size="lg"
      closeOnClickOutside={!loading && removingUserId === null}
      closeOnEscape={!loading && removingUserId === null}
      closeButtonProps={{ loading: loading || removingUserId !== null }}
    >
      <Stack spacing={20}>
        <Divider color="gray.0" />

        {/* List of delegated users */}
        <Stack spacing={8}>
          <P1Medium color="gray.8">Delegovaní používatelia</P1Medium>
          {localDelegatedUsers.length > 0 ? (
            <Stack spacing={8}>
              {localDelegatedUsers.map((user) => {
                const stateColor = getStateColor(user.state);
                const isRemoving = removingUserId === user.userId;

                return (
                  <Flex
                    key={user.userId}
                    align="center"
                    justify="space-between"
                    p={12}
                    sx={(theme) => ({
                      border: `1px solid ${theme.colors.gray[2]}`,
                      borderRadius: 8,
                      backgroundColor: theme.colors.gray[0],
                    })}
                  >
                    <Group spacing={12} noWrap>
                      <Avatar fullName={user.fullName} size="m" bgColor="#6366F1" />
                      <Stack spacing={0}>
                        <P2Medium color="gray.8">{user.fullName}</P2Medium>
                        <P3Medium color="gray.6">{user.email}</P3Medium>
                      </Stack>
                    </Group>

                    <Group spacing={8} noWrap>
                      <Flex
                        px={12}
                        py={4}
                        sx={(theme) => ({
                          borderRadius: 16,
                          backgroundColor:
                            theme.colors[stateColor.bg.split('.')[0]][Number(stateColor.bg.split('.')[1])],
                          border: `1px solid ${
                            theme.colors[stateColor.border.split('.')[0]][Number(stateColor.border.split('.')[1])]
                          }`,
                        })}
                      >
                        <P3Medium color={`${stateColor.text.split('.')[0]}.${stateColor.text.split('.')[1]}`}>
                          {getStateLabel(user.state)}
                        </P3Medium>
                      </Flex>

                      {user.state === 'rejected' && (
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleReDelegate(user.userId)}
                          disabled={isRemoving}
                          loading={isRemoving}
                        >
                          <IconRefresh size={18} />
                        </ActionIcon>
                      )}

                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleRemove(user.userId)}
                        disabled={isRemoving}
                        loading={isRemoving}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Group>
                  </Flex>
                );
              })}
            </Stack>
          ) : (
            <Box>
              <P2Regular>Zatiaľ neboli pridaní žiadni používatelia.</P2Regular>
            </Box>
          )}
        </Stack>

        {/* Add new delegation */}
        <Group spacing={16} align="flex-end" noWrap>
          <RevisionTechnicianSelect
            kind={revisionKind}
            label={
              <P2Regular>{revisionKind === 'revision' ? 'Delegovať revíznu správu' : 'Delegovať formulár'}</P2Regular>
            }
            placeholder={revisionKind === 'revision' ? 'Vyberte technika' : 'Vyberte vypracovateľa'}
            value={selectedTechnicianId}
            onChange={setSelectedTechnicianId}
            searchable
            departmentId={departmentId}
            deviceTypeId={deviceTypeId}
            excludeTechnicianIds={excludeTechnicianIds}
            allowNoCertificate
            w="100%"
          />
          <Button
            variant="primary"
            size="md"
            onClick={loading ? noop : handleDelegate}
            disabled={!selectedTechnicianId || removingUserId !== null}
            loading={loading}
          >
            Delegovať
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
